import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from "semantic-ui-react";
import { Link } from "react-router";
import conditionsCMS from "../../../../cms/treatment/treatment-lite.json";
import { NonStaticSearch } from "./NonStaticSearch.jsx";

const concatenatedConditions = [...conditionsCMS, { n: "Menopause", title: "Menopause", loc: "treatment/menopause" }];

const ConditionsMenu = ({ onMenuChange, showMenu, conditions }) => {
	const [searchResult, setSearchResult] = useState([]);
	const [searching, setSearching] = useState(false);

	const commonConditions = useMemo(() => {
		return conditions.common;
	}, []);

	const alphabeticalConditions = useMemo(
		() =>
			concatenatedConditions
				.map(condition => ({
					...condition,
					title: condition.title.replace(/Treatment\s*/i, "").trim(),
				}))
				.sort((a, b) => a.title.localeCompare(b.title))
				.reduce((acc, condition) => {
					const letter = condition.title.charAt(0).toUpperCase();
					if (!acc[letter]) {
						acc[letter] = [];
					}
					acc[letter].push(condition);
					return acc;
				}, {}),
		[],
	);

	const handleOnSearch = useCallback(value => {
		setSearching(true);

		if (!value.length) {
			setSearching(false);
			setSearchResult([]);
			return;
		}
		const matchedConditions =
			concatenatedConditions.filter(c => c.title.toLowerCase().includes(value.toLowerCase())) || [];
		setSearchResult(matchedConditions);
	}, []);

	useEffect(() => {
		if (!showMenu) {
			setSearchResult([]);
			setSearching(false);
		}
	}, [showMenu]);

	/*
    For SEO some contentful conditions page titles have words like "treatment" and "online"
    We don't want to see these in the menu - rather than make a field in contentful for menu name
    it's easier to just strip these words out here
    */
	const formatTitle = title => {
		return title
			.replace(/Treatment\s*/i, "")
			.replace(/Online\s*/i, "")
			.trim();
	};

	return (
		<div className="conditions-menu space-y-8 pt-10">
			<section>
				{showMenu && (
					<NonStaticSearch
						isStaticPage={!showMenu}
						props={{
							variant: "secondary",
							onSearch: handleOnSearch,
							onClear: () => setSearchResult([]),
							placeholder: "Search for condition"
						}}
					/>
				)}
			</section>

			{/* search results */}
			<section>
				{searchResult.length ? (
					<>
						<div>
							<ul data-testid="condition-search-results" className="flex flex-col gap-2 space-y-2 border-t border-t-is2-blue-50">
								{searchResult.map((condition, index) => (
									<li
										key={index}
										className="text-is2_md text-is2-gray-800 hover:text-is2-gray-600 cursor-pointer border-b border-b-is2-blue-50 !py-3 !mt-0"
									>
										<Link
											to={condition.loc}
											onClick={_ => {
												onMenuChange(false);
											}}
										>
											{formatTitle(condition.title)}
										</Link>
									</li>
								))}
							</ul>
						</div>
					</>
				) : null}

				{searching ? (
					<div className="flex flex-col bg-is2-blue-50 items-center justify-center py-5 mt-6 rounded-md">
						<p className="font-semibold">Can't find your condition?</p>
						<Button
							href={`${app.consts.routes.cat_route}/pharm/DocCons`}
							secondary
							onClick={() => {
								onMenuChange(false);
							}}
						>
							Speak to a doctor - ${app.site_status?.prs_mc["dc_std"]}
						</Button>
					</div>
				) : null}
			</section>

			{!searchResult.length && (
				<>
					<section className="common-conditions">
						<h3 className="text-lg font-semibold text-is2-gray-700 mb-4">Common conditions</h3>
						<div className="flex flex-col space-y-2 border-t border-t-is2-blue-50">
							{commonConditions.map((condition, index) => (
								<Link
									to={condition.url}
									onClick={e => {
										onMenuChange(false);
									}}
									key={index}
									className="text-is2_md text-is2-gray-800 hover:text-is2-gray-600 cursor-pointer border-b border-b-is2-blue-50 !py-3 !mt-0"
								>
									{condition.n}
								</Link>
							))}
						</div>
					</section>

					<section className="alphabetical-conditions">
						<h3 className="text-lg font-semibold text-gray-800 mb-4 text-center">A-Z</h3>
						<div className="space-y-6">
							{Object.entries(alphabeticalConditions).map(([letter, conditions]) => (
								<div key={letter} className="condition-group">
									<h4 className="text-base font-bold text-is2-gray-700 mb-2 pl-2">{letter}</h4>
									<div className="flex flex-col space-y-1 border-t border-t-is2-blue-50">
										{conditions.map((condition, index) => (
											<Link
												to={condition.loc}
												onClick={() => {
													onMenuChange(false);
												}}
												key={index}
												className="text-is2_md text-is2-gray-800 hover:text-is2-gray-600 cursor-pointer border-b border-b-is2-blue-50 !py-3 !mt-0"
											>
												{formatTitle(condition.title)}
											</Link>
										))}
									</div>
								</div>
							))}
						</div>
					</section>
				</>
			)}
		</div>
	);
};

export default ConditionsMenu;
